/* Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved. */

// Import the class from the vendor package (not the heavy `@/service/gql`
// adapter) so this detector stays a dependency-light leaf; `@/service/gql`
// re-exports the same class, so `instanceof` matches app-thrown errors either way.
import { GqlError } from '@amzn/bf-gql'

/**
 * Auth/entitlement denial detection: the Firefly transport surfaces an
 * unauthorized (anonymous or tier-denied) content request as a `GqlError`
 * whose printed message carries the upstream text — `Unauthorized` or a
 * `401` code. This is the WIRE auth signal, not network-vs-server error
 * classification.
 */
export function isUnauthorizedGqlError(err: unknown): err is GqlError {
    if (!(err instanceof GqlError)) return false
    const msg = err.message
    return msg.includes('Unauthorized') || msg.includes('401')
}

export type AuthRecoveryKind = 'sessionExpired' | 'profileUnavailable'

const AUTH_RECOVERY_BY_ERROR_CODE: ReadonlyMap<string, AuthRecoveryKind> = new Map([
    ['AUTH_INVALID_PROFILE_NOT_ASSOCIATED_WITH_MUSIC_PCA', 'profileUnavailable'],
    ['AUTH_INVALID_PROFILE_ID', 'profileUnavailable'],
    ['INVALID_PROFILE_OR_DEVICE_ID', 'profileUnavailable'],
    ['AUTH_INVALID_SERIALIZATION', 'sessionExpired'],
    ['AUTH_INVALID_TYPE', 'sessionExpired'],
    ['AUTH_INVALID_CUSTOMER_ID', 'sessionExpired'],
    // A lapsed session keeps querying with the anonymous key, which Firefly
    // rejects as not allow-listed; signing in is exactly the fix.
    ['AUTH_CLIENT_NOT_ANONYMOUS_ALLOW_LISTED', 'sessionExpired'],
])

// Auth rejections signing in again cannot fix, so a prompt would loop.
const UNRECOVERABLE_AUTH_ERROR_CODES: ReadonlySet<string> = new Set([
    'AUTH_INVALID_CLIENT_ID',
    'AUTH_INVALID_DEVICE_ID',
    'AUTH_INVALID_MUSIC_TERRITORY',
    'AUTH_INVALID_SESSION_ID',
    'RETRIEVE_BENEFITS_FAILED',
    'AUTH_GUEST_BENEFIT_EXPIRED',
])

function fireflyErrorCodes(err: GqlError): string[] {
    return err.errors.flatMap((e) => {
        const code = (e.extensions as { code?: unknown } | undefined)?.code
        return typeof code === 'string' ? [code] : []
    })
}

/**
 * The recovery to offer the customer when Firefly rejects the session, or
 * `undefined` when the error is not one they can act on. Only a genuine
 * transport-level 401 qualifies: `runGqlFetch` sets `httpStatus` only when
 * converting a non-2xx response, and a partially resolved response carrying
 * auth codes is routine for an anonymous page that still rendered. Within a
 * 401, any recoverable code picks the recovery (its position in the list is
 * not contractual), an unrecoverable auth code offers none, and no auth code
 * at all means the credentials are gone.
 */
export function authRecoveryKindOf(err: unknown): AuthRecoveryKind | undefined {
    if (!(err instanceof GqlError) || err.httpStatus !== 401) return undefined
    const codes = fireflyErrorCodes(err)
    for (const code of codes) {
        const kind = AUTH_RECOVERY_BY_ERROR_CODE.get(code)
        if (kind) return kind
    }
    return codes.some((code) => UNRECOVERABLE_AUTH_ERROR_CODES.has(code))
        ? undefined
        : 'sessionExpired'
}

// Firefly server-fault codes. A 5xx loses its wire status in the
// RequestError → GqlError conversion (see runGqlFetch), so the Firefly
// error code is the only server-fault signal left on the surfaced GqlError.
const SERVER_FAULT_CODES: ReadonlySet<string> = new Set(['INTERNAL', 'SERVICE_ERROR'])

/**
 * Server-fault detection: a Firefly 5xx surfaces as a `GqlError` whose first
 * error carries an `INTERNAL`/`SERVICE_ERROR` extensions code.
 */
export function isServerFaultGqlError(err: unknown): err is GqlError {
    return (
        err instanceof GqlError &&
        fireflyErrorCodes(err).some((code) => SERVER_FAULT_CODES.has(code))
    )
}
